import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CampaignData {
  id: number;
  name: string;
  objective?: string | null;
  effectiveStatus?: string | null;
  dailyBudget?: string | null;
  metrics: {
    spend: string;
    impressions: number;
    clicks: number;
    purchases: number;
    roas: string;
    cpa: string;
    ctr: string;
  };
}

interface CampaignTableProps {
  campaigns: CampaignData[];
  onRowClick?: (campaign: CampaignData) => void;
  limit?: number;
}

const statusColors: Record<string, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  PAUSED: "bg-gray-100 text-gray-600",
  DELETED: "bg-red-100 text-red-700",
  ARCHIVED: "bg-gray-100 text-gray-500",
};

const objectiveColors: Record<string, string> = {
  CONVERSIONS: "bg-indigo-100 text-indigo-700",
  OUTCOME_SALES: "bg-indigo-100 text-indigo-700",
  TRAFFIC: "bg-blue-100 text-blue-700",
  OUTCOME_TRAFFIC: "bg-blue-100 text-blue-700",
  AWARENESS: "bg-purple-100 text-purple-700",
  OUTCOME_AWARENESS: "bg-purple-100 text-purple-700",
  LEAD_GENERATION: "bg-green-100 text-green-700",
  OUTCOME_LEADS: "bg-green-100 text-green-700",
};

export function CampaignTable({ campaigns, onRowClick, limit }: CampaignTableProps) {
  const [sortField, setSortField] = useState<string>("spend");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const sorted = [...campaigns].sort((a, b) => {
    const aVal = parseFloat((a.metrics as any)[sortField] || "0");
    const bVal = parseFloat((b.metrics as any)[sortField] || "0");
    return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
  });

  const displayed = limit ? sorted.slice(0, limit) : sorted;

  return (
    <div className="overflow-x-auto" data-testid="campaign-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="font-semibold">Campaign</TableHead>
            <TableHead>Objective</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>
              <Button variant="ghost" size="sm" className="h-auto p-0 font-semibold" onClick={() => toggleSort("spend")}>
                Spend <ArrowUpDown className="h-3 w-3 ml-1" />
              </Button>
            </TableHead>
            <TableHead>
              <Button variant="ghost" size="sm" className="h-auto p-0 font-semibold" onClick={() => toggleSort("roas")}>
                ROAS <ArrowUpDown className="h-3 w-3 ml-1" />
              </Button>
            </TableHead>
            <TableHead>
              <Button variant="ghost" size="sm" className="h-auto p-0 font-semibold" onClick={() => toggleSort("cpa")}>
                CPA <ArrowUpDown className="h-3 w-3 ml-1" />
              </Button>
            </TableHead>
            <TableHead>
              <Button variant="ghost" size="sm" className="h-auto p-0 font-semibold" onClick={() => toggleSort("ctr")}>
                CTR <ArrowUpDown className="h-3 w-3 ml-1" />
              </Button>
            </TableHead>
            <TableHead>Purchases</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayed.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-gray-400 py-8">No campaigns found</TableCell>
            </TableRow>
          ) : (
            displayed.map(c => (
              <TableRow
                key={c.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onRowClick?.(c)}
                data-testid={`campaign-row-${c.id}`}
              >
                <TableCell className="font-medium max-w-[200px] truncate">{c.name}</TableCell>
                <TableCell>
                  {c.objective && (
                    <Badge variant="secondary" className={`text-xs ${objectiveColors[c.objective] || "bg-gray-100"}`}>
                      {c.objective.replace("OUTCOME_", "").replace(/_/g, " ")}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {c.effectiveStatus && (
                    <Badge variant="secondary" className={`text-xs ${statusColors[c.effectiveStatus] || "bg-gray-100"}`}>
                      {c.effectiveStatus}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-mono text-sm">${parseFloat(c.metrics.spend).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                <TableCell className="font-mono text-sm">{parseFloat(c.metrics.roas).toFixed(2)}x</TableCell>
                <TableCell className="font-mono text-sm">${parseFloat(c.metrics.cpa).toFixed(2)}</TableCell>
                <TableCell className="font-mono text-sm">{parseFloat(c.metrics.ctr).toFixed(2)}%</TableCell>
                <TableCell className="font-mono text-sm">{c.metrics.purchases}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
